import React, {useState} from 'react';
import {Box, Typography, Grid, Tabs, Tab, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import {PageHeader} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {READINESS_BAND_COLORS} from '../constants';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedKpi, isSuppressedBenchmark, isSuppressedCell, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

import {BandChip as SharedBandChip} from '../../DashboardCommon/widgets.comp';
import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems, sortBars} from '../../DashboardCommon/barSort';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
// Secondary benchmark-marker colour (Global) — kept distinct from NAVY and
// from every tier/band fill colour used on the bars themselves.
const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';

/* Canonical 8 readiness areas, in fixed display order — matches
   Class/School/Individual dashboards' Readiness pages. Area scores must
   render in this order (not ranked by score), each labeled with its
   "Readiness Area N" code. */
const READINESS_AREA_NAMES = [
    'Youth AI-Digital Activities',
    'Self-Regulated AI-Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
    'Authentic and Ethical Digital Identity',
    'Critical Information Management',
];

/* ── Shared legend ──────────────────────────────────────────────── */
// Rubric band colored chips (High/Moderate/Basic/Low) + Global avg marker —
// replaces the earlier plain "National score" color swatch per the deck's
// own "Replace this with rubric band colored chips." annotation.
const BarLegend = ({centered = false}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: centered ? 'center' : 'flex-start'}}>
        {Object.keys(READINESS_BAND_COLORS).map((band) => (
            <BandChip key={band} label={band} color={READINESS_BAND_COLORS[band]}/>
        ))}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
        </Box>
    </Box>
);

/* ── Band chip (matches School Dashboard's BandChip pill) ────────── */
const BandChip = ({label, color}) => <SharedBandChip label={label} color={color || READINESS_BAND_COLORS[label] || ORANGE}/>;

/* ── Shared progress bar ────────────────────────────────────────── */
const ScoreBar = ({score, tier, benchmark, height = '12px', radius = '6px'}) => {
    const tierColor = READINESS_BAND_COLORS[tier] || ORANGE;
    return (
        <Box sx={{height, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: radius, position: 'relative', flex: 1}}>
            <Tooltip title={fmtValue(score)} arrow placement="top">
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${score ?? 0}%`, backgroundColor: tierColor, borderRadius: radius, cursor: 'pointer',
                }}/>
            </Tooltip>
            {benchmark != null && (
                <Tooltip title={benchmarkText('Global avg', benchmark)} arrow placement="bottom">
                    <Box sx={{
                        position: 'absolute',
                        left: `${benchmark}%`,
                        bottom: '-9px',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderBottom: `9px solid ${MARKER_SECONDARY}`,
                        transform: 'translateX(-50%)',
                        cursor: 'pointer',
                        zIndex: 4,
                    }}/>
                </Tooltip>
            )}
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 1 — OVERVIEW                                               */
/* ════════════════════════════════════════════════════════════════ */

// A KPI under the n floor (suppressed) reads "—" with the reason underneath and no band.
const SummaryCard = ({label, score, tier, suppressed}) => {
    const tierColor = suppressed ? '#D1D5DB' : (READINESS_BAND_COLORS[tier] || ORANGE);
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '12px',
            backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Box sx={{backgroundColor: tierColor, height: '6px', flexShrink: 0}}/>
            <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', flex: 1}}>
                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                    {label}
                </Typography>
                <Typography sx={{fontSize: {xs: '32px', md: '40px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                    {suppressed || score === null ? '—' : score}
                </Typography>
                {suppressed && <Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mt: 0.75}}>{SUPPRESSED_COPY}</Typography>}
                {!suppressed && tier && (
                    <Box sx={{mt: 'auto', pt: 1.5}}>
                        <BandChip label={tier} color={tierColor}/>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
const OverviewTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_3,S_4' });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    // Defensively locate section S_4 and S_3
    let s4Section = null;
    let s3Section = null;

    if (dashboardData) {
        if (dashboardData.section_code === 'S_4') {
            s4Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s4Section = dashboardData.cards.sections.find(s => s.section_code === 'S_4');
        } else if (Array.isArray(dashboardData.sections)) {
            s4Section = dashboardData.sections.find(s => s.section_code === 'S_4');
        }

        if (dashboardData.section_code === 'S_3') {
            s3Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s3Section = dashboardData.cards.sections.find(s => s.section_code === 'S_3');
        } else if (Array.isArray(dashboardData.sections)) {
            s3Section = dashboardData.sections.find(s => s.section_code === 'S_3');
        }
    }

    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }

    if (!dashboardData || !s4Section) {
        return <NoDataAvailable />;
    }

    const s4Cards = s4Section.cards || [];

    const summaryCards = s4Cards
        .filter(c => c.card_type === 'KPICard')
        .map(c => ({
            label: c.kpi_name,
            suppressed: isSuppressedKpi(c),
            score: !isSuppressedKpi(c) && c.kpi_value != null ? Math.round(c.kpi_value) : null,
            tier: isSuppressedKpi(c) ? null : c.kpi_band
        }));

    // Find C_6 in S_3 to populate area scores
    const s3Cards = s3Section?.cards || [];
    const c6Card = s3Cards.find(c => c.card_code === 'C_6' && c.card_type === 'BarChartBMCard');
    const items = c6Card?.items || [];

    // Keep the 8 canonical readiness areas in their fixed display order —
    // not ranked by score — each labeled with its "Readiness Area N" code.
    const areaScores = READINESS_AREA_NAMES.map((name, index) => {
        const item = items.find(it => it.label === name);
        if (!item) return null;
        const suppressed = isSuppressedItem(item);                 // under the n floor: placeholder row
        const bench = item.benchmark_values?.[0];
        return {
            areaCode: `Readiness Area ${index + 1}`,
            label: item.label,
            suppressed,
            score: !suppressed && item.value != null ? Math.round(item.value) : null,
            tier: suppressed ? null : item.band,
            benchmark: bench && !isSuppressedBenchmark(bench) && bench.value != null ? Math.round(bench.value) : null
        };
    }).filter(Boolean);
    // Group order is the framework order above; the value modes rank the eight areas (suppressed ones last).
    const shownAreas = sortItems(areaScores, sortMode);

    // Guidance text / HTML
    let lowestAreaName = "";
    if (items.length > 0) {
        // The lowest area is picked among the areas that show a score.
        const sortedItems = items.filter((it) => !isSuppressedItem(it) && it.value != null).sort((a, b) => a.value - b.value);
        lowestAreaName = sortedItems[0]?.label || "";
    }

    let htmlContent = s4Cards.find(c => c.card_type === 'LeftLineCard')?.leftline_html || '';
    if (htmlContent) {
        htmlContent = htmlContent.replace('{Lowest Area}', lowestAreaName);
    }

    return (
        <Box>
            <PageHeader
                title="Child Online Safety Readiness Overview"
                subtitle="Which child online safety readiness areas need policy attention?"
            />

            {/* Summary cards */}
            <Grid container spacing={2.5} sx={{mb: 3}}>
                {summaryCards.map(c => (
                    <Grid item xs={12} md={12 / (summaryCards.length || 1)} key={c.label}>
                        <SummaryCard {...c}/>
                    </Grid>
                ))}
            </Grid>

            {/* Area score bars */}
            {areaScores.length > 0 && (
                <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, mb: 3}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                            Readiness Area Scores
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                            <BarLegend/>
                            <SortToggle value={sortMode} onChange={setSortMode}/>
                        </Box>
                    </Box>
                    {shownAreas.map(t => (
                        <Box key={t.label} sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
                            <Box sx={{width: {xs: 130, md: 220}, flexShrink: 0}}>
                                <Typography sx={{fontSize: '12px', color: '#6A7282', lineHeight: 1.3}}>{t.areaCode}</Typography>
                                <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{t.label}</Typography>
                            </Box>
                            {t.suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
                            <ScoreBar score={t.score} tier={t.tier} benchmark={t.benchmark} height="16px" radius="8px"/>
                            <Typography sx={{fontSize: '16px', fontWeight: 800, color: NAVY, width: 34, textAlign: 'right', flexShrink: 0}}>{t.score ?? '—'}</Typography>
                            <Box sx={{flexShrink: 0}}>{t.tier && <BandChip label={t.tier}/>}</Box>
                            </>)}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Decision guidance (hidden) */}
            {/* {htmlContent && (
                <Box 
                    sx={{
                        border: '1px solid #C7D9E8',
                        borderLeft: `4px solid ${NAVY}`,
                        borderRadius: '8px',
                        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)',
                        p: 2.5,
                        '& ul': { m: 0, pl: 2.5 },
                        '& li': { mb: 0.8, fontSize: '13px', color: '#374151', lineHeight: 1.6 },
                        '& p': { m: 0, fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1.2 },
                        '& strong': { color: NAVY, fontWeight: 700 }
                    }}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            )} */}
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 2 — INDICATOR PROFILE                                      */
/* ════════════════════════════════════════════════════════════════ */

// An item under the n floor (suppressed) shows the placeholder in place of its bar, score and band.
const IndicatorRow = ({itemLabel, name, score, tier, benchmark, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.2, pl: 4}}>
        <Box sx={{width: {xs: 160, md: 220}, flexShrink: 0}}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>{itemLabel}</Typography>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{name}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        <ScoreBar score={score} tier={tier} benchmark={benchmark} height="12px" radius="6px"/>
        <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY, width: 30, textAlign: 'right', flexShrink: 0}}>{score ?? '—'}</Typography>
        <Box sx={{flexShrink: 0}}>{tier && <BandChip label={tier}/>}</Box>
        </>)}
    </Box>
);

const InsightCard = ({title, items, accentColor}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        height: '100%',
    }}>
        <Box sx={{backgroundColor: accentColor, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                {title}
            </Typography>
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((item, i) => (
                    <Box component="li" key={i} sx={{mb: 1}}>
                        <Typography sx={{fontSize: '13.5px', color: '#374151', lineHeight: 1.5}}>
                            {item.name}{item.score === null || item.score === undefined ? '' : ` (${item.score})`}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const GuidanceBox = ({items, text}) => (
    <Box sx={{
        border: '1px solid #C7D9E8',
        borderLeft: `4px solid ${NAVY}`,
        borderRadius: '8px',
        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)',
        p: 2.5,
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1.2}}>
            Decision guidance:
        </Typography>
        {text && (
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{text}</Typography>
        )}
        {items && (
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((item, i) => (
                    <Box component="li" key={i} sx={{mb: 0.8}}>
                        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{item}</Typography>
                    </Box>
                ))}
            </Box>
        )}
    </Box>
);

/* ── Guidance banner (matches School Dashboard's teal GuidanceBanner) ─ */
const GuidanceBanner = ({label = 'Recommended action:', text, html, items, emphasis}) => (
    <Box sx={{
        mt: 3, p: {xs: 2, md: 2.5}, backgroundColor: 'var(--brand-surface-subtle, #F0F7F4)', border: '1px solid #C6E0D8',
        borderLeft: '4px solid #1B6B55', borderRadius: '0 8px 8px 0',
        '& p': {m: 0, fontSize: '13px', color: '#374151', lineHeight: 1.7},
        '& ul': {m: 0, pl: 2.5},
        '& li': {fontSize: '13px', color: '#374151', lineHeight: 1.7},
        '& strong': {color: '#1B6B55', fontWeight: 700},
    }}>
        {html ? (
            <Typography component="div" sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}} dangerouslySetInnerHTML={{__html: html}}/>
        ) : items ? (
            <>
                {label && (
                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: '#1B6B55', mb: 1.2}}>
                        {label}
                    </Typography>
                )}
                <Box component="ul" sx={{m: 0, pl: 2.5, mb: emphasis ? 1.5 : 0}}>
                    {items.map((item, i) => (
                        <Box component="li" key={i} sx={{mb: 0.8}}>
                            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{item}</Typography>
                        </Box>
                    ))}
                </Box>
                {emphasis && (
                    <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#1B6B55', lineHeight: 1.6}}>
                        {emphasis}
                    </Typography>
                )}
            </>
        ) : text ? (
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                {label && <strong style={{color: '#1B6B55'}}>{label}&nbsp;</strong>}{text}
            </Typography>
        ) : null}
    </Box>
);

const IndicatorProfileTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_4a' });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    // Defensively locate section S_4a
    let s4aSection = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_4a') {
            s4aSection = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s4aSection = dashboardData.cards.sections.find(s => s.section_code === 'S_4a');
        } else if (Array.isArray(dashboardData.sections)) {
            s4aSection = dashboardData.sections.find(s => s.section_code === 'S_4a');
        }
    }

    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }

    if (!dashboardData || !s4aSection) {
        return <NoDataAvailable />;
    }

    const cards = s4aSection.cards || [];

    const chartCard = cards.find(c => c.card_type === 'BarChartBMCard') || {};
    const chartItems = chartCard.items || [];

    const mappedIndicators = chartItems.map((item, idx) => {
        const bench = item.benchmark_values?.[0];
        const benchmarkValue = bench && !isSuppressedBenchmark(bench) && bench.value != null ? Math.round(bench.value) : null;
        const suppressed = isSuppressedItem(item);                 // under the n floor: placeholder row
        const itemNum = item.sublabel ? parseInt(item.sublabel.match(/\d+/)?.[0], 10) : NaN;
        const areaNum = item.parent_sublabel ? parseInt(item.parent_sublabel.match(/\d+/)?.[0], 10) : NaN;
        return {
            id: item.code || `COS ${String(idx + 1).padStart(2, '0')}`,
            itemNum: Number.isFinite(itemNum) ? itemNum : idx + 1,
            areaNum: Number.isFinite(areaNum) ? areaNum : 1,
            itemLabel: '',                       // numbered below, 1…N straight through the areas (QA 9 Oct)
            areaTitle: `Readiness Area ${Number.isFinite(areaNum) ? areaNum : 1}`,
            parentLabel: item.parent_label || 'Readiness Area',
            name: item.label,
            suppressed,
            score: !suppressed && item.value != null ? Math.round(item.value) : null,
            tier: suppressed ? null : item.band,
            benchmark: benchmarkValue
        };
    });

    // Group indicators by parentLabel (readiness area), then sort areas and
    // items within each area numerically — insertion order from the API
    // isn't guaranteed to already be in Area 1, 2, 3... sequence.
    const areasMap = new Map();
    mappedIndicators.forEach((it) => {
        const groupKey = it.parentLabel;
        if (!areasMap.has(groupKey)) {
            areasMap.set(groupKey, {areaTitle: it.areaTitle, parentLabel: it.parentLabel, areaNum: it.areaNum, items: []});
        }
        areasMap.get(groupKey).items.push(it);
    });
    const groupedAreas = Array.from(areasMap.values()).sort((a, b) => a.areaNum - b.areaNum);
    groupedAreas.forEach((g) => g.items.sort((a, b) => a.itemNum - b.itemNum));
    // Items are numbered in the order shown, continuing across areas ("Readiness Item 1 … 30"), whatever the card's own sublabel says.
    let itemNo = 0;
    groupedAreas.forEach((g) => g.items.forEach((it) => { itemNo += 1; it.itemLabel = `Readiness Item ${itemNo}`; }));
    const shownGroups = sortBars(groupedAreas, sortMode);

    // Rankings run over the items that show a score (items under the n floor take no part);
    // when none does, the lists say why.
    const scoredItems = chartItems.filter((item) => !isSuppressedItem(item) && item.value != null);
    const noneShown = chartItems.length > 0 && scoredItems.length === 0 ? [{name: SUPPRESSED_COPY, score: null}] : [];
    // Strengths: top 3
    const strengths = scoredItems.length ? [...scoredItems]
        .sort((a, b) => b.value - a.value)
        .slice(0, 3)
        .map(item => ({name: item.label, score: Math.round(item.value)})) : noneShown;

    // Ascending by raw value — shared by Needs Attention and the guidance sentence
    // so the sentence always names the first Needs Attention item.
    const ascItems = [...scoredItems].sort((a, b) => a.value - b.value);

    // Needs Attention: bottom 3
    const needsAttention = scoredItems.length ? ascItems
        .slice(0, 3)
        .map(item => ({name: item.label, score: Math.round(item.value)})) : noneShown;

    return (
        <Box>
            <PageHeader
                title="Readiness Item Profile"
                subtitle="Which readiness items need national attention?"
            />

            <Grid container spacing={3} alignItems="flex-start">
                {/* Left — indicator list */}
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                                {chartCard.title || ""}
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <BarLegend/>
                                <SortToggle value={sortMode} onChange={setSortMode}/>
                            </Box>
                        </Box>
                        {shownGroups.map((areaGroup, idx) => (
                            <Box key={areaGroup.parentLabel || 'all'} sx={{mb: idx < shownGroups.length - 1 ? 3.5 : 0}}>
                                {(areaGroup.areaTitle || areaGroup.parentLabel) && <Box sx={{mb: 2}}>
                                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>
                                        {areaGroup.areaTitle}
                                    </Typography>
                                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>
                                        {areaGroup.parentLabel}
                                    </Typography>
                                </Box>}
                                {areaGroup.items.map((ind) => (
                                    <IndicatorRow key={ind.id} {...ind} itemLabel={areaGroup.flattened && ind.groupLabel ? `${ind.itemLabel} · ${ind.groupLabel}` : ind.itemLabel}/>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Grid>

                {/* Right — insight panels */}
                <Grid item xs={12} md={4}>
                    <InsightCard
                        title="Strengths"
                        items={strengths}
                        accentColor="#1B6B55"
                    />
                    <Box sx={{mt: 2.5}}>
                        <InsightCard
                            title="Needs Attention"
                            items={needsAttention}
                            accentColor="#E04535"
                        />
                    </Box>
                    {ascItems.length > 0 && (
                        <GuidanceBanner
                            label="Decision guidance:"
                            text={`${ascItems[0].label} is the lowest item. Start with a single national module on it; once that is in place, use the regional heatmap to target implementation support where the gap is widest.`}
                        />
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 3 — REGIONAL COMPETENCY                                    */
/* ════════════════════════════════════════════════════════════════ */

const scoreToBand = (v) => {
    const val = v || 0;
    if (val >= 75) return 'High';
    if (val >= 50) return 'Moderate';
    if (val >= 25) return 'Basic';
    return 'Low';
};

const getBandColor = (band) => {
    if (!band || typeof band !== 'string') return null;
    const lowerBand = band.toLowerCase();
    const matchedKey = Object.keys(READINESS_BAND_COLORS).find(
        (key) => key.toLowerCase() === lowerBand
    );
    return matchedKey ? READINESS_BAND_COLORS[matchedKey] : null;
};

const getCellBg = (band, val) => {
    // No band and no value (a cell that is not sent) stays uncoloured rather than reading as "Low".
    const color = getBandColor(band) || (val === null || val === undefined ? null : READINESS_BAND_COLORS[scoreToBand(val)]);
    if (!color) return 'transparent';
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
};

const heatCell = {textAlign: 'center', py: 1.6, px: 2, borderBottom: '1px solid #F3F4F6', fontSize: '14px', fontWeight: 700, color: NAVY};
const heatHeadCell = {backgroundColor: NAVY, color: '#fff', fontSize: '13px', fontWeight: 700, borderBottom: 'none', py: 1.75, px: 2};

// cell_data carries {band, n, suppressed}; a region under the n floor shows the placeholder, uncoloured.
const heatCellOf = (col, ri) => {
    const cd = col.bands?.[ri];
    const sup = !!cd && typeof cd === 'object' && isSuppressedCell(cd);
    const band = typeof cd === 'string' ? cd : cd?.band;
    return {sup, band, val: sup ? null : col.values[ri]};
};

// The heat-map sits in its own component so the sort/filter hook runs below the tab's loading
// branches (QA 9 Oct 2026). Rows are the regions; each area column reads its score through an
// accessor — null under the n floor, so such cells sort last and drop out of a range filter.
const RegionHeatmapTable = ({regions, heatmapRows}) => {
    const rows = React.useMemo(() => regions.map((region, ri) => ({region, ri})), [regions]);
    const columns = React.useMemo(() => [
        {key: 'region', label: 'Region', type: 'text'},
        ...heatmapRows.map((col) => ({key: col.label, label: col.label, type: 'number', accessor: (r) => heatCellOf(col, r.ri).val})),
    ], [heatmapRows]);
    const t = useTableControls(rows, columns);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                <Table sx={{minWidth: 650}}>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('region')} sx={{...heatHeadCell, textAlign: 'left'}}/>
                            {heatmapRows.map((col) => (
                                <SortFilterHeaderCell key={col.label} {...t.headerProps(col.label)} sx={{...heatHeadCell, textAlign: 'center'}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + heatmapRows.length} sx={{...heatCell, fontWeight: 400, fontSize: '13px', color: '#6A7282', py: 3}}>No region matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map(({region, ri}) => (
                            <TableRow key={region}>
                                <TableCell sx={{...heatCell, textAlign: 'left', backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                    {region}
                                </TableCell>
                                {heatmapRows.map((col) => {
                                    const {sup, band, val} = heatCellOf(col, ri);
                                    return (
                                        <TableCell key={col.label} sx={{...heatCell, backgroundColor: sup ? 'transparent' : getCellBg(band, val)}}>
                                            {sup ? <SuppressedValue variant="cell"/> : fmtValue(val)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

/* Static decision guidance for the By Region tab (deck copy; no data placeholders). */
const REGION_GUIDANCE_ITEMS = [
    'If a weak pattern appears across many regions, choose national intervention.',
    'If concentrated in one region, target regional implementation support.',
    'Do not compare regions with low participation or suppressed data.',
];
const REGION_GUIDANCE_EMPHASIS = 'Prioritize regional support where weak competency coincides with high risk and adequate participation.';

const RegionalCompetencyTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_4b' });

    // Defensively locate section S_4b
    let s4bSection = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_4b') {
            s4bSection = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s4bSection = dashboardData.cards.sections.find(s => s.section_code === 'S_4b');
        } else if (Array.isArray(dashboardData.sections)) {
            s4bSection = dashboardData.sections.find(s => s.section_code === 'S_4b');
        }
    }

    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }

    if (!dashboardData || !s4bSection) {
        return <NoDataAvailable />;
    }

    const cards = s4bSection.cards || [];

    const tableCard = cards.find(c => c.card_type === 'TableRubricCard') || {};
    const tableObjects = tableCard.table_objects || [];
    
    const stateColumn = tableObjects.find(col => col.column_type === 'text' || col.column_name === 'State');
    const regions = stateColumn ? stateColumn.values : [];

    // Column headers use a short "Readiness Area N" label (like School's
    // "By Grade" heatmap) — the full descriptive name goes in the legend
    // below the table instead, not in the header itself.
    let areaCounter = 0;
    const heatmapRows = tableObjects
        .filter(col => col.column_type === 'rubric')
        .map((col) => {
            const isComposite = col.column_name === 'Composite';
            let label = col.column_name;
            if (!isComposite) {
                areaCounter += 1;
                const subname = (col.column_subname !== undefined && col.column_subname !== null) ? String(col.column_subname).trim() : '';
                const numMatch = subname.match(/\d+/);
                const areaNum = numMatch ? numMatch[0] : areaCounter;
                label = `Readiness Area ${areaNum}`;
            }
            return {
                label,
                name: col.column_name,
                values: col.values || [],
                bands: col.bands || col.cell_data || []
            };
        });

    // "Readiness Area" legend — maps each "Readiness Area N" column to its
    // real descriptive name (excludes the Composite column).
    const readinessAreaLegend = heatmapRows
        .map(row => {
            const areaNum = row.label?.match(/\d+/)?.[0];
            if (!areaNum) return null;
            return {
                label: row.label,
                name: row.name || row.label
            };
        })
        .filter(Boolean);

    return (
        <Box>
            <PageHeader
                title="Readiness by Region"
                subtitle="Where are the regional readiness gaps?"
            />

            <Box>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                    {tableCard.title || ""}
                </Typography>

                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap'}}>
                    {Object.keys(READINESS_BAND_COLORS).map((band) => (
                        <BandChip key={band} label={band} color={READINESS_BAND_COLORS[band]}/>
                    ))}
                </Box>

                <RegionHeatmapTable regions={regions} heatmapRows={heatmapRows}/>
            </Box>

            {readinessAreaLegend.length > 0 && (
                <Box sx={{mt: 3.5, mb: 1}}>
                    <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5}}>
                        Readiness Area
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, pl: 2}}>
                        {readinessAreaLegend.map((area) => (
                            <Typography key={area.label} sx={{fontSize: '14px', color: '#1F2937'}}>
                                <strong style={{fontWeight: 700, color: '#000000'}}>{area.label}:</strong> {area.name}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Decision guidance */}
            <GuidanceBanner label="Decision guidance:" items={REGION_GUIDANCE_ITEMS} emphasis={REGION_GUIDANCE_EMPHASIS}/>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                  */
/* ════════════════════════════════════════════════════════════════ */

const CompetencySection = () => {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <NationalDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 1,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="Overview"/>
                    <Tab label="Item Profile"/>
                    <Tab label="By Region"/>
                </Tabs>
            }
        >
            {activeTab === 0 && <OverviewTab/>}
            {activeTab === 1 && <IndicatorProfileTab/>}
            {activeTab === 2 && <RegionalCompetencyTab/>}
        </NationalDashboardShell>
    );
};

export {OverviewTab, IndicatorProfileTab, RegionalCompetencyTab};
export default CompetencySection;
